/** Refresh this many seconds before the token actually expires. */
const EXPIRY_SKEW_SECONDS = 30;

type Fetcher = () => Promise<string>;

let token: string | null = null;
let expiresAt = 0;
let inFlight: Promise<string | null> | null = null;
let fetcher: Fetcher | null = null;

export function setAccessTokenFetcher(next: Fetcher): void {
  fetcher = next;
}

export function clearAccessToken(): void {
  token = null;
  expiresAt = 0;
  inFlight = null;
}

/**
 * JWT segments are base64**url**: `-`/`_` instead of `+`/`/`, and no `=` padding.
 * `atob` throws on those characters, and whether a given token contains them depends on
 * the payload's byte alignment — so this silently returned 0 for a fraction of tokens
 * (non-ASCII payloads only, e.g. a name with Vietnamese diacritics), which made every
 * request re-mint a JWT against the rate-limited `GET /api/auth/token`.
 */
function readExpiry(jwt: string): number {
  try {
    const [, payload] = jwt.split('.');
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(padded)) as { exp?: number };
    return typeof decoded.exp === 'number' ? decoded.exp : 0;
  } catch {
    return 0;
  }
}

/**
 * The access token lives only in this module. Everything that needs it awaits here,
 * so a page load, a burst of parallel queries, and a post-401 retry all share one
 * request to the auth service rather than racing three.
 */
export async function getAccessToken(): Promise<string | null> {
  const now = Math.floor(Date.now() / 1000);
  if (token && expiresAt - EXPIRY_SKEW_SECONDS > now) {
    return token;
  }
  if (inFlight) {
    return inFlight;
  }
  if (!fetcher) {
    return null;
  }

  inFlight = (async () => {
    try {
      const next = await fetcher!();
      token = next;
      expiresAt = readExpiry(next);
      return token;
    } catch {
      token = null;
      expiresAt = 0;
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
